import { Paper } from '@mui/material';
import type { GetContractsResponse } from '@weft/api-definition';
import { isEmptyString, withDefault } from '@weft/utils';
import { useConnectedIntegrations } from 'apps/app/src/hooks/requests/use-integrations';
import { sortBy } from 'lodash';
import React from 'react';
import { withSuspenseBoundary } from '../../../../../components/utility/SuspenseBoundary';
import { useContracts } from '../../../../../hooks/requests/use-contracts';
import { usePageParam } from '../../../../../hooks/search-params/use-page-param';
import { usePageSizeParam } from '../../../../../hooks/search-params/use-page-size-param';
import { usePaginationResetHandler } from '../../../../../hooks/use-pagination-reset-handler';
import { useSearchWithSearchParams } from '../../../../../hooks/use-search-with-search-params';
import { ContractsTable } from '../ContractsTable';
import { useContractStatusesParam } from '../hooks/use-contract-statuses-param';
import { useContractSyncStatusesParam } from '../hooks/use-contract-sync-statuses-params';
import { PlansHeaderSummarize } from '../PlansHeaderSummarize';
import { ContractsTableToolbar } from './contracts-tab-content-components/ContractsTableToolbar';

const ContractsTabContent: React.FC = () => {
  useConnectedIntegrations();
  const { data: contractsResponse, isFetching } = useTableContracts();
  const { contracts, ...summarizeData } = contractsResponse;

  usePaginationResetHandler(
    withDefault(contractsResponse?.countedFilteredContracts, 0),
  );

  return (
    <>
      <div className="flex h-full flex-col">
        <PlansHeaderSummarize data={summarizeData as Omit<GetContractsResponse, 'contracts'>} />
        <Paper
          elevation={1}
          sx={{
            height: '100%',
            borderRadius: '12px',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <ContractsTableToolbar />
          <ContractsTable
            loadingItems={isFetching}
            items={contractsResponse.contracts}
            itemsCount={withDefault(
              contractsResponse?.countedFilteredContracts,
              0,
            )}
          />
        </Paper>
      </div>
    </>
  );
};

const ContractsTabContentWithSuspenseBoundary =
  withSuspenseBoundary(ContractsTabContent);
export { ContractsTabContentWithSuspenseBoundary as ContractsTabContent };

function useTableContracts() {
  const [page] = usePageParam();
  const [pageSize] = usePageSizeParam();
  const [contractStatuses, _setContractStatuses] = useContractStatusesParam();
  const [contractSyncStatuses, _setContractSyncStatuses] = useContractSyncStatusesParam();
  const [query] = useSearchWithSearchParams();

  const { data: contractsResponse, ...restContractsResponse } = useContracts({
    page,
    pageSize,
    search: isEmptyString(query) ? undefined : query,
    contractStatuses: JSON.stringify(withDefault(contractStatuses, [])),
    syncStatuses: JSON.stringify(withDefault(contractSyncStatuses, [])),
  });
  const sortedContracts = sortBy(
    withDefault(contractsResponse?.contracts, []),
    (contract) => contract.createdAt,
  );
  const reversedContracts = [...sortedContracts].reverse();

  return {
    ...restContractsResponse,
    data: {
      ...contractsResponse,
      contracts: reversedContracts,
    },
  };
}
